// src/components/Views/Helpers/TeaserImages.jsx
// Styling via global.scss (.teaser-images__container / __image)

import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import Image from '@plone/volto/components/theme/Image/Image';
import { hasImage } from '../../../helpers/content';

/**
 * TeaserImages - Displays up to two teaser images side by side (CSS Grid, 50% each).
 * Caption is shown as an overlay on hover. Copyright is always visible (bottom right).
 *
 * Props:
 *   image1, image2             – Plone NamedBlobImage objects
 *   caption1/2                 – Optional caption text (hover overlay)
 *   copyright1/2               – Optional copyright text (always visible)
 *   copyright1BlackFont/2      – If true, copyright is rendered in black instead of white
 *   alt                        – Alt text fallback
 *
 * Pass `content` to take the item's teaser fields, or the props one by one.
 */
// The tiles are a fixed 16:9 box filled with object-fit: cover, which is
// right for a photograph and wrong for a logo: the CENTAURO wordmark is
// 330x60, so filling the box scales it to three times the tile's width and
// shows a third of it. A picture this far from the tile's shape is not a
// photograph that happens to be cropped - it is one that cannot survive
// being cropped, so it is fitted into the tile instead.
//
// Decided from the image's own dimensions, which the field already carries,
// so nothing has to be set per image. The threshold is the same one the
// backend uses for lead images (academic.types' focal_point subscriber): at
// 2.4:1 a 16:9 crop already discards a quarter of the width.
const MAX_CROPPABLE_RATIO = 2.4;

const fitsWholeImage = (image) => {
  const { width, height } = image || {};
  if (!width || !height) return false;
  const ratio = width / height;
  return ratio > MAX_CROPPABLE_RATIO || ratio < 1 / MAX_CROPPABLE_RATIO;
};

/** One image tile (used twice below, for image1 and image2). */
const TeaserImage = ({
  image,
  caption,
  copyright,
  copyrightBlackFont,
  alt,
}) => (
  <div
    className={`teaser-images__image${
      fitsWholeImage(image) ? ' teaser-images__image--whole' : ''
    }`}
  >
    <Image
      src={flattenToAppURL(
        image.scales?.preview?.download ||
          image.scales?.mini?.download ||
          image.download,
      )}
      alt={caption || alt}
      loading="lazy"
      decoding="async"
    />
    {caption && (
      <div className="teaser-images__overlay">
        <span className="teaser-images__caption">{caption}</span>
      </div>
    )}
    {copyright && (
      <span
        className={`teaser-images__copyright-badge${copyrightBlackFont ? ' teaser-images__copyright-badge--black' : ''}`}
      >
        © {copyright}
      </span>
    )}
  </div>
);

TeaserImage.propTypes = {
  image: PropTypes.shape({
    download: PropTypes.string,
    scales: PropTypes.object,
    width: PropTypes.number,
    height: PropTypes.number,
  }).isRequired,
  caption: PropTypes.string,
  copyright: PropTypes.string,
  copyrightBlackFont: PropTypes.bool,
  alt: PropTypes.string,
};

/** The two teaser images of a content item, with their captions. */
const fromContent = (content) => {
  const tile = (n) =>
    hasImage(content[`teaser_image${n}`]) && {
      image: content[`teaser_image${n}`],
      caption: content[`teaser_image${n}_caption`] || '',
      copyright: content[`teaser_image${n}_copyright`] || '',
      copyrightBlackFont: !!content[`teaser_image${n}_copyright_black_font`],
    };
  const [first, second] = [tile(1), tile(2)].filter(Boolean);
  return {
    image1: first?.image,
    caption1: first?.caption,
    copyright1: first?.copyright,
    copyright1BlackFont: first?.copyrightBlackFont,
    image2: second?.image,
    caption2: second?.caption,
    copyright2: second?.copyright,
    copyright2BlackFont: second?.copyrightBlackFont,
    alt: content.title,
  };
};

const TeaserImages = ({ content, ...props }) => {
  const {
    image1,
    image2,
    caption1 = '',
    copyright1 = '',
    copyright1BlackFont = false,
    caption2 = '',
    copyright2 = '',
    copyright2BlackFont = false,
    alt = '',
  } = content ? fromContent(content) : props;
  if (!image1 && !image2) return null;

  const hasOnlyOneImage = (image1 && !image2) || (!image1 && image2);

  return (
    <div className="teaser-images">
      <div
        className={`teaser-images__container${hasOnlyOneImage ? ' teaser-images__container--single' : ''}`}
      >
        {image1 && (
          <TeaserImage
            image={image1}
            caption={caption1}
            copyright={copyright1}
            copyrightBlackFont={copyright1BlackFont}
            alt={alt}
          />
        )}
        {image2 && (
          <TeaserImage
            image={image2}
            caption={caption2}
            copyright={copyright2}
            copyrightBlackFont={copyright2BlackFont}
            alt={alt}
          />
        )}
      </div>
    </div>
  );
};

TeaserImages.propTypes = {
  /** A content item: its teaser_image1/2 fields are used, empty ones skipped. */
  content: PropTypes.objectOf(PropTypes.any),
  image1: PropTypes.shape({
    download: PropTypes.string,
    scales: PropTypes.object,
  }),
  image2: PropTypes.shape({
    download: PropTypes.string,
    scales: PropTypes.object,
  }),
  caption1: PropTypes.string,
  copyright1: PropTypes.string,
  copyright1BlackFont: PropTypes.bool,
  caption2: PropTypes.string,
  copyright2: PropTypes.string,
  copyright2BlackFont: PropTypes.bool,
  alt: PropTypes.string,
};

export default memo(TeaserImages);
